import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../Context/AuthContext'
import { useCart } from '../Context/CartContext'
import { useCurrency } from '../Context/CurrencyContext'
import useTemplateScripts from '../hooks/useTemplateScripts'
import { getUserOrders } from '../services/orderService'
import { classifyCategory } from '../constants/shopCategories'
import { CONTACT_INFO, SITE_LINKS } from '../constants/siteLinks'
import MainHeader from './MainHeader'
import HomeFooter from './HomeFooter'
import CartDrawer from './CartDrawer'
import './Orders.css'

const STATUS_TABS = [
  { id: 'all', label: 'All orders' },
  { id: 'pending', label: 'Placed' },
  { id: 'processing', label: 'Processing' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'cancelled', label: 'Cancelled' },
]

const IN_PROGRESS = ['pending', 'processing', 'shipped']

/** Tracker steps; store pickups read "Ready for pickup" / "Collected" instead of shipping wording. */
const trackerSteps = (order) => {
  const pickup = order.deliveryMethod === 'store'
  return [
    { id: 'pending', icon: 'fa-receipt', label: 'Order placed' },
    { id: 'processing', icon: 'fa-box-open', label: 'Processing' },
    { id: 'shipped', icon: pickup ? 'fa-store' : 'fa-truck-fast', label: pickup ? 'Ready for pickup' : 'On the way' },
    { id: 'delivered', icon: 'fa-house-circle-check', label: pickup ? 'Collected' : 'Delivered' },
  ]
}

const STATUS_BADGE = {
  pending: { tone: 'info', label: 'Placed' },
  processing: { tone: 'info', label: 'Processing' },
  shipped: { tone: 'blue', label: 'Shipped' },
  delivered: { tone: 'success', label: 'Delivered' },
  cancelled: { tone: 'danger', label: 'Cancelled' },
}

const PAYMENT_BADGE = {
  paid: { tone: 'success', icon: 'fa-circle-check', label: 'Paid' },
  pending: { tone: 'warn', icon: 'fa-clock', label: 'Awaiting payment' },
  failed: { tone: 'danger', icon: 'fa-circle-xmark', label: 'Payment failed' },
}

/** One sentence on where the order is now. */
const statusMessage = (order) => {
  const pickup = order.deliveryMethod === 'store'
  switch (order.status) {
    case 'pending':
      return order.paymentStatus === 'paid'
        ? 'We’ve received your order and payment. Our team will confirm it shortly.'
        : 'Your order is waiting for payment. Contact us if you have already paid.'
    case 'processing':
      return 'We’re preparing your order and checking every item.'
    case 'shipped':
      return pickup ? 'Your order is ready — collect it at our store in Remera.' : 'Your order is on its way to you.'
    case 'delivered':
      return pickup ? 'You collected this order. Thank you for shopping with us!' : 'Your order was delivered. Thank you for shopping with us!'
    case 'cancelled':
      return 'This order was cancelled. Contact us if you have any questions.'
    default:
      return ''
  }
}

const formatDate = (value, withTime = false) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  })
}

const itemCount = (order) => order.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)

const paymentLabel = (order) => {
  const method = order.paymentMethod || 'Mobile Money'
  return order.momoProvider ? `${method} · ${order.momoProvider.toUpperCase()}` : method
}

function Orders() {
  useTemplateScripts()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, token, isAuthenticated, loading: authLoading } = useAuth()
  const { cartItems, addToCart } = useCart()
  const { formatPrice } = useCurrency()
  const userId = user?.id || user?._id || null

  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState('all')
  const [query, setQuery] = useState('')
  const [openIds, setOpenIds] = useState(() => new Set(searchParams.get('order') ? [searchParams.get('order')] : []))
  const [showCart, setShowCart] = useState(false)
  const [toast, setToast] = useState(null)

  // Signed-in customers only (wait for the saved session to load first)
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !userId)) {
      navigate(SITE_LINKS.login, { replace: true, state: { from: '/orders', message: 'Please sign in to view your orders.' } })
    }
  }, [authLoading, isAuthenticated, userId, navigate])

  const load = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    setError('')
    try {
      const list = await getUserOrders(userId, token)
      setOrders([...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)))
    } catch (err) {
      setError(err.message || 'We couldn’t load your orders.')
    } finally {
      setLoading(false)
    }
  }, [userId, token])

  useEffect(() => {
    if (!authLoading && isAuthenticated) load()
  }, [authLoading, isAuthenticated, load])

  useEffect(() => {
    if (!toast) return undefined
    const timer = setTimeout(() => setToast(null), 2400)
    return () => clearTimeout(timer)
  }, [toast])

  // An order linked from checkout (?order=ID) scrolls into view once loaded
  useEffect(() => {
    const target = searchParams.get('order')
    if (!target || loading) return
    document.getElementById(`order-${target}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [loading, searchParams])

  const counts = useMemo(() => {
    const result = { all: orders.length }
    orders.forEach((order) => {
      result[order.status] = (result[order.status] || 0) + 1
    })
    return result
  }, [orders])

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase()
    return orders.filter((order) => {
      if (tab !== 'all' && order.status !== tab) return false
      if (!term) return true
      return (
        String(order.id).toLowerCase().includes(term) ||
        order.items.some((item) => String(item.name || '').toLowerCase().includes(term))
      )
    })
  }, [orders, tab, query])

  if (!isAuthenticated) return null

  const inProgress = orders.filter((order) => IN_PROGRESS.includes(order.status)).length
  const totalSpent = orders
    .filter((order) => order.paymentStatus === 'paid' && order.status !== 'cancelled')
    .reduce((sum, order) => sum + (Number(order.total) || 0), 0)

  const toggle = (id) =>
    setOpenIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const buyAgain = (order) => {
    const available = order.items.filter((item) => item.productId)
    if (!available.length) {
      setToast('These products are no longer available')
      return
    }
    available.forEach((item) =>
      addToCart(
        { id: item.productId, name: item.name, title: item.title, price: item.price, image: item.image, category: item.category },
        item.quantity || 1
      )
    )
    setToast(`${available.length} ${available.length === 1 ? 'product' : 'products'} added to your cart`)
    setShowCart(true)
  }

  return (
    <div className="or-page">
      <MainHeader cartCount={cartItems.length} onCartClick={() => setShowCart((open) => !open)} />

      <main className="or-main">
        <div className="or-container">
          <section className="or-hero">
            <div className="or-hero__copy">
              <nav className="or-crumbs" aria-label="Breadcrumb">
                <Link to={SITE_LINKS.home}>Home</Link>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <span>My account</span>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <span aria-current="page">My Orders</span>
              </nav>
              <div className="or-hero__heading">
                <span className="or-hero__icon" aria-hidden="true">
                  <i className="fa-solid fa-box" />
                </span>
                <div>
                  <h1 className="or-hero__title">My Orders</h1>
                  <p className="or-hero__text">
                    Track your orders, check payment and delivery status, and buy your favourites again.
                  </p>
                </div>
              </div>
            </div>

            <dl className="or-stats" aria-label="Order summary">
              <div>
                <dt>
                  <i className="fa-solid fa-bag-shopping" aria-hidden="true" />
                  Total orders
                </dt>
                <dd>{loading ? '—' : orders.length}</dd>
              </div>
              <div>
                <dt>
                  <i className="fa-solid fa-truck-fast" aria-hidden="true" />
                  In progress
                </dt>
                <dd>{loading ? '—' : inProgress}</dd>
              </div>
              <div>
                <dt>
                  <i className="fa-solid fa-wallet" aria-hidden="true" />
                  Total paid
                </dt>
                <dd>{loading ? '—' : formatPrice(totalSpent)}</dd>
              </div>
            </dl>
          </section>

          {!loading && !error && orders.length > 0 && (
            <div className="or-toolbar">
              <div className="or-tabs" role="tablist" aria-label="Filter orders by status">
                {STATUS_TABS.filter((item) => item.id === 'all' || counts[item.id]).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === item.id}
                    className={`or-tab${tab === item.id ? ' is-active' : ''}`}
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                    <span className="or-tab__count">{counts[item.id] || 0}</span>
                  </button>
                ))}
              </div>
              <label className="or-search">
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by order number or product"
                  aria-label="Search orders"
                />
              </label>
            </div>
          )}

          {loading ? (
            <ul className="or-list" aria-busy="true" aria-label="Loading orders">
              {Array.from({ length: 3 }, (_, i) => (
                <li key={i} className="or-card or-card--skeleton">
                  <span className="or-skel or-skel--short" />
                  <span className="or-skel" />
                  <span className="or-skel or-skel--bar" />
                </li>
              ))}
            </ul>
          ) : error ? (
            <div className="or-state">
              <span className="or-state__icon is-error" aria-hidden="true">
                <i className="fa-solid fa-triangle-exclamation" />
              </span>
              <h2>We couldn’t load your orders</h2>
              <p>{error}</p>
              <button type="button" className="or-btn or-btn--primary" onClick={load}>
                <i className="fa-solid fa-rotate" aria-hidden="true" />
                Try again
              </button>
            </div>
          ) : orders.length === 0 ? (
            <div className="or-state">
              <span className="or-state__icon" aria-hidden="true">
                <i className="fa-solid fa-box-open" />
              </span>
              <h2>No orders yet</h2>
              <p>When you place an order, it will appear here so you can follow its payment and delivery.</p>
              <Link to={SITE_LINKS.shop} className="or-btn or-btn--primary">
                <i className="fa-solid fa-bag-shopping" aria-hidden="true" />
                Start shopping
              </Link>
            </div>
          ) : visible.length === 0 ? (
            <div className="or-state or-state--compact">
              <span className="or-state__icon" aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass" />
              </span>
              <h2>No matching orders</h2>
              <p>Try another order number or product name, or show all orders.</p>
              <button
                type="button"
                className="or-btn or-btn--outline"
                onClick={() => {
                  setTab('all')
                  setQuery('')
                }}
              >
                Show all orders
              </button>
            </div>
          ) : (
            <ul className="or-list">
              {visible.map((order) => {
                const open = openIds.has(String(order.id))
                const status = STATUS_BADGE[order.status] || STATUS_BADGE.pending
                const payment = PAYMENT_BADGE[order.paymentStatus] || PAYMENT_BADGE.pending
                const steps = trackerSteps(order)
                const reached = steps.findIndex((step) => step.id === order.status)
                const count = itemCount(order)
                const pickup = order.deliveryMethod === 'store'

                return (
                  <li key={order.id} id={`order-${order.id}`} className={`or-card${open ? ' is-open' : ''}`}>
                    <header className="or-card__head">
                      <div className="or-card__id">
                        <span className="or-card__label">Order</span>
                        <strong>#{order.id}</strong>
                      </div>
                      <dl className="or-card__meta">
                        <div>
                          <dt>Placed on</dt>
                          <dd>{formatDate(order.createdAt)}</dd>
                        </div>
                        <div>
                          <dt>Items</dt>
                          <dd>{count}</dd>
                        </div>
                        <div>
                          <dt>Total</dt>
                          <dd className="or-card__total">{formatPrice(order.total)}</dd>
                        </div>
                      </dl>
                      <div className="or-card__badges">
                        <span className={`or-badge is-${status.tone}`}>{status.label}</span>
                        <span className={`or-badge or-badge--soft is-${payment.tone}`}>
                          <i className={`fa-solid ${payment.icon}`} aria-hidden="true" />
                          {payment.label}
                        </span>
                      </div>
                    </header>

                    <div className="or-card__body">
                      <div className="or-card__summary">
                        <ul className="or-thumbs" aria-label="Products in this order">
                          {order.items.slice(0, 3).map((item, index) => (
                            <li key={item._id || item.productId || index} title={item.name}>
                              <img
                                src={item.image}
                                alt={item.name}
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder-image.png'
                                }}
                              />
                              {item.quantity > 1 && <span className="or-thumbs__qty">×{item.quantity}</span>}
                            </li>
                          ))}
                          {order.items.length > 3 && <li className="or-thumbs__more">+{order.items.length - 3}</li>}
                        </ul>
                        <div className="or-card__status">
                          <p className="or-card__names">
                            {order.items
                              .slice(0, 2)
                              .map((item) => item.name)
                              .join(', ')}
                            {order.items.length > 2 ? ` and ${order.items.length - 2} more` : ''}
                          </p>
                          <p className={`or-card__message is-${status.tone}`}>{statusMessage(order)}</p>
                        </div>
                      </div>

                      {order.status === 'cancelled' ? (
                        <div className="or-cancelled">
                          <i className="fa-solid fa-ban" aria-hidden="true" />
                          Cancelled{order.cancelledAt ? ` on ${formatDate(order.cancelledAt)}` : ''}
                        </div>
                      ) : (
                        <ol className="or-track" aria-label="Order progress">
                          {steps.map((step, index) => {
                            const done = index < reached || order.status === 'delivered'
                            const current = index === reached && order.status !== 'delivered'
                            return (
                              <li
                                key={step.id}
                                className={`or-track__step${done ? ' is-done' : ''}${current ? ' is-current' : ''}`}
                                aria-current={current ? 'step' : undefined}
                              >
                                <span className="or-track__dot" aria-hidden="true">
                                  <i className={`fa-solid ${done ? 'fa-check' : step.icon}`} />
                                </span>
                                <span className="or-track__label">{step.label}</span>
                              </li>
                            )
                          })}
                        </ol>
                      )}

                      <div className="or-card__actions">
                        <button
                          type="button"
                          className="or-btn or-btn--outline"
                          onClick={() => toggle(String(order.id))}
                          aria-expanded={open}
                          aria-controls={`order-details-${order.id}`}
                        >
                          {open ? 'Hide details' : 'View details'}
                          <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
                        </button>
                        <button type="button" className="or-btn or-btn--primary" onClick={() => buyAgain(order)}>
                          <i className="fa-solid fa-rotate-right" aria-hidden="true" />
                          Buy again
                        </button>
                      </div>
                    </div>

                    {open && (
                      <div className="or-details" id={`order-details-${order.id}`}>
                        <div className="or-details__items">
                          <h3>Products</h3>
                          <ul>
                            {order.items.map((item, index) => {
                              const type = item.category ? classifyCategory(item.category, item.name).subcategoryName : ''
                              const content = (
                                <>
                                  <span className="or-line__thumb">
                                    <img
                                      src={item.image}
                                      alt=""
                                      loading="lazy"
                                      onError={(e) => {
                                        e.currentTarget.src = '/placeholder-image.png'
                                      }}
                                    />
                                  </span>
                                  <span className="or-line__info">
                                    {type && <span className="or-line__type">{type}</span>}
                                    <span className="or-line__name">{item.name}</span>
                                    <span className="or-line__qty">
                                      {item.quantity} × {formatPrice(item.price)}
                                    </span>
                                  </span>
                                  <strong className="or-line__total">{formatPrice((Number(item.price) || 0) * (Number(item.quantity) || 1))}</strong>
                                </>
                              )
                              return (
                                <li key={item._id || item.productId || index}>
                                  {item.productId ? (
                                    <Link to={`/product-details/${item.productId}`} className="or-line">
                                      {content}
                                    </Link>
                                  ) : (
                                    <div className="or-line">{content}</div>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        </div>

                        <aside className="or-details__side">
                          <div className="or-info">
                            <h3>
                              <i className={`fa-solid ${pickup ? 'fa-store' : 'fa-truck-fast'}`} aria-hidden="true" />
                              {pickup ? 'Store pickup' : 'Delivery'}
                            </h3>
                            <p>{pickup ? `${CONTACT_INFO.address}, Kigali` : order.shippingAddress || '—'}</p>
                            {order.customerName && <p className="or-info__muted">{order.customerName}</p>}
                            {order.phone && <p className="or-info__muted">{order.phone}</p>}
                          </div>

                          <div className="or-info">
                            <h3>
                              <i className="fa-solid fa-credit-card" aria-hidden="true" />
                              Payment
                            </h3>
                            <p>{paymentLabel(order)}</p>
                            <p className={`or-info__status is-${payment.tone}`}>
                              <i className={`fa-solid ${payment.icon}`} aria-hidden="true" />
                              {payment.label}
                            </p>
                          </div>

                          <dl className="or-totals">
                            <div>
                              <dt>Subtotal</dt>
                              <dd>{formatPrice(order.subtotal)}</dd>
                            </div>
                            <div>
                              <dt>{pickup ? 'Pickup' : 'Delivery'}</dt>
                              <dd>{Number(order.shippingFee) > 0 ? formatPrice(order.shippingFee) : 'Free'}</dd>
                            </div>
                            {Number(order.discount) > 0 && (
                              <div>
                                <dt>Discount</dt>
                                <dd>−{formatPrice(order.discount)}</dd>
                              </div>
                            )}
                            <div className="or-totals__grand">
                              <dt>Total</dt>
                              <dd>{formatPrice(order.total)}</dd>
                            </div>
                          </dl>

                          <a href={CONTACT_INFO.phoneHref} className="or-help">
                            <i className="fa-solid fa-headset" aria-hidden="true" />
                            <span>
                              Questions about this order?
                              <strong>Call {CONTACT_INFO.phone}</strong>
                            </span>
                          </a>
                          <p className="or-details__updated">Last updated {formatDate(order.updatedAt, true)}</p>
                        </aside>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </main>

      <HomeFooter />
      <CartDrawer open={showCart} onClose={() => setShowCart(false)} onToast={setToast} />

      {toast && (
        <div className="or-toast" role="status">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
          {toast}
        </div>
      )}
    </div>
  )
}

export default Orders
